// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3). Primary workflow layout.
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { HeaderV2Component } from '../../../core/common/components/workflow-v2/header-v2/header-v2.component';
import { PARAMS } from '../../../core/common/constants/routes.constant';
import { RootState } from '../../../core/store/reducers/RootState.model';

import { CURRENT_STATUS } from '../../../core/common/constants/status.constant';
import { BlockingInformation } from '../../../core/common/models/workflow/diagnosis-session';

import { DialogCommonService } from '../../../core/services/common/dialog-common.service';
import { RouterService } from '../../../core/services/router.service';
import { loadDataByDiagnosisId } from '../../../core/store/workflow/actions/workflow.actions';
import {
  selectBlockingInfo,
  selectSessionStatus,
} from '../../../core/store/workflow/selectors/workflow.selectors';
import { MailBoxComponent } from './mail-box/mail-box.component';

@Component({
  selector: 'app-workflow-layout',
  imports: [HeaderV2Component, MailBoxComponent, RouterOutlet],
  templateUrl: './workflow-layout.component.html',
  styleUrl: './workflow-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowLayoutComponent implements OnInit, OnDestroy {
  #store = inject(Store<RootState>);
  readonly #activeRoute = inject(ActivatedRoute);
  readonly #ngRouter = inject(Router);
  readonly #authService = inject(MsalService);

  #dialogCommonService = inject(DialogCommonService);
  #router = inject(RouterService);
  sessionStatus = this.#store.select(selectSessionStatus);

  sessionBlocking$ = this.#store.select(selectBlockingInfo);
  BLOCKING_TIME = 10;
  sessionBlocking?: BlockingInformation;

  userName = '';
  labelAvatar = '';
  diagnosisId = '';
  #destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.getActiveAccountInfo();
    this.getDiagnosisId();
    this.goToRelatedPage();
    this.verfifySessionValid();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  getDiagnosisId() {
    // WorkflowLayoutComponent is shared across sibling routes (/pid/:projectId, /sid/:sessionId,
    // /diagnosis-complete/:id) and is NOT recreated when navigating between them, so a one-time
    // `firstChild.params` subscription captured here would go stale the moment the user first
    // lands on a route without a diagnosisId param (Mode 3's /pid/:projectId landing page).
    // Re-derive the leaf route's params on every completed navigation instead.
    this.#ngRouter.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.#destroy$),
      )
      .subscribe(() => this.#syncDiagnosisId());

    this.#syncDiagnosisId();
  }

  #syncDiagnosisId(): void {
    let route = this.#activeRoute.snapshot;
    while (route.firstChild) route = route.firstChild;

    const diagnosisId = route.params[PARAMS.DiagnosisId];
    if (diagnosisId && diagnosisId !== this.diagnosisId) {
      this.diagnosisId = diagnosisId;
      this.#store.dispatch(loadDataByDiagnosisId({ diagnosisId }));
    }
  }

  getActiveAccountInfo(): void {
    const activeAccount = this.#authService.instance.getActiveAccount();

    this.userName = activeAccount?.name ? activeAccount.name : '';
    //Get first letter from user name
    this.labelAvatar = this.userName
      .split(' ')
      .map((item) => item[0])
      .join('')
      .substring(0, 2);
  }

  goToRelatedPage() {
    this.sessionStatus
      .pipe(
        filter((status) => !!status),
        take(1),
      )
      .subscribe((status) => {
        this.#router.goByStatus(this.diagnosisId, status);
      });
  }

  verfifySessionValid() {
    this.sessionBlocking$
      .pipe(
        filter((info) => !!info.modifiedAt),
        takeUntil(this.#destroy$),
      )
      .subscribe((blockingInformation) => {
        if (blockingInformation) {
          this.sessionBlocking = blockingInformation;
          this.checkSessionState(this.userName);
        }
      });
  }

  checkSessionState(username: string) {
    if (this.sessionBlocking?.sessionStatus === CURRENT_STATUS.CANCELLED) {
      this.openWarningDialog(
        'Cancelled Session',
        `This session has been cancelled by [${this.sessionBlocking!.modifiedBy}]. Access is temporarily unavailable.`,
      );
    } else if (this.isSessionBlocking(username)) {
      this.openWarningDialog(
        'Blocking session',
        `This session has been blocked by [${this.sessionBlocking!.modifiedBy}]. Try again later.`,
      );
    }
  }

  isSessionBlocking(username: string): boolean {
    if (this.sessionBlocking) {
      const lastModified = new Date(
        this.sessionBlocking!.modifiedAt!,
      ).getTime();
      const expirationTime = lastModified + this.BLOCKING_TIME * 60000;
      const currentTime = this.getCurrentUTCDate().getTime();

      return (
        username !== '' &&
        username.toLowerCase() !==
          this.sessionBlocking.modifiedBy.toLowerCase() &&
        this.sessionBlocking.isBlocking &&
        expirationTime > currentTime
      );
    }
    return false;
  }

  private getCurrentUTCDate() {
    const current = new Date();
    return new Date(current.getTime() + current.getTimezoneOffset() * 60000);
  }

  openWarningDialog(title: string, message: string) {
    this.#dialogCommonService.show(true, {
      title,
      message,
      closeLabel: 'Close',
      closable: false,
    });
  }
}
